"use client";

import { useEffect, useState } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { AssetStoryblok } from "@/generated/sb/sb-types";

/**
 * The personal details collected on the first gift card step. These never go
 * through Centra directly — the gift-certificate line item only carries an
 * amount. We keep them locally (sessionStorage, same lifetime as a normal
 * checkout draft) until the payment step, where they're folded into the
 * `giftMessage` string Centra does accept.
 */
export interface GiftCardDetails {
	senderName: string;
	receiverName: string;
	receiverEmail?: string;
	message?: string;
}

interface GiftCardFlowStore {
	details: GiftCardDetails | null;
	setDetails: (details: GiftCardDetails) => void;
	// The `giftcard` block's optional image field. Step one (the block) is the
	// only place that has this — a CMS field — so it's carried in the same
	// sessionStorage-persisted store to keep showing on the dedicated
	// /giftcard/address and /giftcard/payment routes.
	image: AssetStoryblok | null;
	setImage: (image: AssetStoryblok | null) => void;
	reset: () => void;
}

const useGiftCardFlowStore = create(
	persist<GiftCardFlowStore>(
		(set) => ({
			details: null,
			setDetails: (details) => set({ details }),
			image: null,
			setImage: (image) => set({ image }),
			reset: () => set({ details: null, image: null }),
		}),
		{
			name: "giftcard-details",
			storage: createJSONStorage(() => sessionStorage),
		},
	),
);

export const useGiftCardDetails = () => useGiftCardFlowStore((state) => state.details);
export const useSetGiftCardDetails = () => useGiftCardFlowStore((state) => state.setDetails);
export const useGiftCardImage = () => useGiftCardFlowStore((state) => state.image);
export const useSetGiftCardImage = () => useGiftCardFlowStore((state) => state.setImage);
export const useResetGiftCardDetails = () => useGiftCardFlowStore((state) => state.reset);

/**
 * Whether this store has finished reading sessionStorage.
 *
 * A generic "have we mounted on the client" flag (e.g. `useHydrated` from
 * `@trydig/ui/hooks`) is NOT the same thing and doesn't gate this reliably —
 * it's an independent async process with no guaranteed ordering against
 * zustand's own storage read. Reading `details`/`image` before *this*
 * finishes can silently see the pre-hydration `null` defaults.
 */
export const useGiftCardFlowHydrated = () => {
	const [hydrated, setHydrated] = useState(() => useGiftCardFlowStore.persist.hasHydrated());

	useEffect(() => {
		if (useGiftCardFlowStore.persist.hasHydrated()) {
			setHydrated(true);
			return;
		}
		return useGiftCardFlowStore.persist.onFinishHydration(() => setHydrated(true));
	}, []);

	return hydrated;
};

/**
 * Centra's gift-certificate payment API only accepts a single free-text
 * `giftMessage` string — there's no separate sender/receiver/message field.
 * Fold the structured details the buyer entered into one note that ends up
 * on the order and the gift certificate itself.
 */
export const formatGiftMessage = ({
	senderName,
	receiverName,
	receiverEmail,
	message,
}: GiftCardDetails): string => {
	// To/from share the first line; the personal message goes beneath, after a
	// blank line, so Klaviyo's `|linebreaks` renders it as its own paragraph.
	const lines = [`To: ${receiverName} - From: ${senderName}`];

	// if (receiverEmail) lines.push(`Receiver email: ${receiverEmail}`);
	if (message) lines.push("", message);

	return lines.join("\n");
};
